import { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axiosInstance, { setAuthHandlers } from '../api/axiosConfig';
import { clearPushSubscription } from '../realtime/pushClient';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null); // Store in memory only
    const [loading, setLoading] = useState(true);
    const tokenRef = useRef(null); // Keep a ref for immediate access in interceptors

    // Update ref whenever token changes
    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    // Get current token (used by axios interceptor)
    const getAccessToken = useCallback(() => {
        return tokenRef.current;
    }, []);

    // Refresh token function (used by axios interceptor and silent refresh)
    const refreshAccessToken = useCallback(async () => {
        try {
            const response = await axiosInstance.post('/api/auth/refresh');

            if (response.data.success && response.data.token) {
                const newToken = response.data.token;
                setToken(newToken);
                tokenRef.current = newToken;
                
                if (response.data.user) {
                    setUser(response.data.user);
                }
                
                return newToken;
            }
            return null;
        } catch (error) {
            // Silent fail - interceptor will handle logout
            return null;
        }
    }, []);

    // Logout function (used by axios interceptor)
    const handleLogout = useCallback(() => {
        setUser(null);
        setToken(null);
        tokenRef.current = null;
        
        // Clean up old localStorage if exists
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }, []);

    // Initialize auth handlers for axios interceptors
    useEffect(() => {
        setAuthHandlers(getAccessToken, refreshAccessToken, handleLogout);
    }, [getAccessToken, refreshAccessToken, handleLogout]);

    // On mount, try to refresh token from httpOnly cookie
    useEffect(() => {
        const initAuth = async () => {
            try {
                const response = await axiosInstance.post('/api/auth/refresh');

                if (response.data.success && response.data.token) {
                    setToken(response.data.token);
                    tokenRef.current = response.data.token;
                    setUser(response.data.user);
                }
            } catch (error) {
                // No valid refresh token - silent fail
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    // Proactively refresh the access token every 12 minutes (token expires in 15m).
    // Prevents expiry during long sessions like meeting recordings where no API
    // calls are made, which would otherwise cause the next request to fail with 401.
    useEffect(() => {
        if (!token) return;
        const interval = setInterval(() => {
            refreshAccessToken();
        }, 12 * 60 * 1000);
        return () => clearInterval(interval);
    }, [token, refreshAccessToken]);

    const login = useCallback((userData, authToken) => {
        setUser(userData);
        setToken(authToken);
        tokenRef.current = authToken;
        
        // Clean up old localStorage tokens if they exist
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }, []);

    const logout = useCallback(async () => {
        try {
            // Drop this browser's push subscription BEFORE the token clears —
            // it needs a valid Bearer token to identify which subscription is
            // "mine". Without this, a shared/kiosk device would keep ringing
            // with the previous user's delivery alerts after they sign out.
            await clearPushSubscription();
            // Call backend to revoke refresh token
            await axiosInstance.post('/api/auth/logout');
        } catch (error) {
            // Ignore logout errors
        } finally {
            handleLogout();
        }
    }, [handleLogout]);

    const updateUser = useCallback((userData) => {
        setUser(userData);
        // Dispatch custom event for same-window updates
        window.dispatchEvent(new CustomEvent('userUpdated', { detail: userData }));
    }, []);

    const value = {
        user,
        token,
        login,
        logout,
        updateUser,
        refreshToken: refreshAccessToken,
        isAuthenticated: !!token,
        loading
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
